import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { Observation } from 'fhir/r4';
import { ObservationReferenceRangeTemplate } from '../../observation-reference-ranges/observation-reference-range.service';
import { ObservationValueDisplayComponent } from '../observation-value-display/observation-value-display.component';

export interface ReferenceRangeConfirmDialogData {
  observation: Observation;
  templates: ObservationReferenceRangeTemplate[];
}

@Component({
  selector: 'app-reference-range-confirm-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatRadioModule,
    MatSelectModule,
    ObservationValueDisplayComponent
  ],
  template: `
    <h2 mat-dialog-title>Confirm Reference Range</h2>
    <mat-dialog-content>
      <div class="summary-box">
        <p class="mat-body-2" style="margin: 0 0 8px 0; color: var(--mat-sys-on-surface-variant);">Entered Observation:</p>
        <p class="obs-name">{{ data.observation.code?.text || data.observation.code?.coding?.[0]?.display || 'Unknown Observation' }}</p>
        <app-observation-value-display [observation]="data.observation"></app-observation-value-display>
      </div>

      <p class="instruction">Select the applicable reference range for this patient:</p>

      <mat-radio-group [(ngModel)]="selectedTemplateId" class="template-group">
        <mat-radio-button [value]="null">
          <strong>None</strong> (Do not attach a range)
        </mat-radio-button>
        
        @for (template of data.templates; track template.id) {
          <mat-radio-button [value]="template.id">
            <div class="template-option">
              <span class="range-type">{{ template.range.type?.coding?.[0]?.display || template.range.type?.text || 'Range' }}:</span>
              <span class="range-bounds">{{ formatBounds(template) }}</span>
              @if (template.range.appliesTo?.length || template.range.age) {
                <span class="range-filters">
                  (Applies to: {{ formatFilters(template) }})
                </span>
              }
            </div>
          </mat-radio-button>
        }
      </mat-radio-group>

      @if (selectedTemplateId) {
        <div class="interpretation-box">
          <p class="instruction" style="margin-top: 0;">Is this result abnormal?</p>
          <mat-form-field appearance="outline" style="width: 100%;">
            <mat-label>Interpretation Flag</mat-label>
            <mat-select [(ngModel)]="selectedInterpretationCode">
              <mat-option [value]="null">Normal / No Flag</mat-option>
              <mat-option value="N">Normal (N)</mat-option>
              <mat-option value="H">High (H)</mat-option>
              <mat-option value="L">Low (L)</mat-option>
              <mat-option value="A">Abnormal (A)</mat-option>
              <mat-option value="HH">Critical High (HH)</mat-option>
              <mat-option value="LL">Critical Low (LL)</mat-option>
              <mat-option value="AA">Critical Abnormal (AA)</mat-option>
              <mat-option value="POS">Positive (POS)</mat-option>
              <mat-option value="NEG">Negative (NEG)</mat-option>
              <mat-option value="IND">Indeterminate (IND)</mat-option>
              <mat-option value="RR">Reactive (RR)</mat-option>
              <mat-option value="NR">Non-reactive (NR)</mat-option>
              <mat-option value="S">Susceptible (S)</mat-option>
              <mat-option value="R">Resistant (R)</mat-option>
              <mat-option value="I">Intermediate (I)</mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="skip()">Skip</button>
      <button mat-raised-button color="primary" (click)="confirm()">Apply & Save</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .summary-box {
      background: var(--mat-sys-surface-container-low, #f5f5f5);
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 24px;
    }
    .obs-name {
      font-weight: 500;
      font-size: 16px;
      margin: 0 0 8px 0;
    }
    .instruction {
      font-weight: 500;
      margin-bottom: 12px;
    }
    .template-group {
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-bottom: 24px;
    }
    .template-option {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px;
      line-height: 1.4;
    }
    .range-type {
      font-weight: 600;
    }
    .range-filters {
      color: var(--mat-sys-on-surface-variant, rgba(0,0,0,0.6));
      font-size: 12px;
    }
    .interpretation-box {
      margin-top: 24px;
      padding-top: 24px;
      border-top: 1px solid var(--mat-sys-outline-variant, rgba(0,0,0,0.1));
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReferenceRangeConfirmDialogComponent {
  selectedTemplateId: string | null = null;
  selectedInterpretationCode: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<ReferenceRangeConfirmDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReferenceRangeConfirmDialogData
  ) {
    // Auto-select the first template if there is exactly one
    if (this.data.templates?.length === 1 && this.data.templates[0].id) {
      this.selectedTemplateId = this.data.templates[0].id;
    }
  }

  formatBounds(template: ObservationReferenceRangeTemplate): string {
    const range = template.range;
    const lowStr = range.low?.value != null ? `${range.low.value} ${range.low.unit || range.low.code || ''}`.trim() : null;
    const highStr = range.high?.value != null ? `${range.high.value} ${range.high.unit || range.high.code || ''}`.trim() : null;
    
    if (lowStr && highStr) return `${lowStr} - ${highStr}`;
    if (lowStr) return `>= ${lowStr}`;
    if (highStr) return `<= ${highStr}`;
    if (range.text) return range.text;
    return 'Configured text';
  }

  formatFilters(template: ObservationReferenceRangeTemplate): string {
    const parts: string[] = [];
    if (template.range.appliesTo?.length) {
      parts.push(template.range.appliesTo.map(c => c.text || c.coding?.[0]?.display).join(', '));
    }
    if (template.range.age?.low || template.range.age?.high) {
      const low = template.range.age.low?.value ? `${template.range.age.low.value} ${template.range.age.low.code || ''}` : '';
      const high = template.range.age.high?.value ? `${template.range.age.high.value} ${template.range.age.high.code || ''}` : '';
      if (low && high) parts.push(`Age ${low} - ${high}`);
      else if (low) parts.push(`Age >= ${low}`);
      else if (high) parts.push(`Age <= ${high}`);
    }
    return parts.join('; ');
  }

  skip(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    if (!this.selectedTemplateId) {
      this.dialogRef.close(null);
      return;
    }

    const template = this.data.templates.find(t => t.id === this.selectedTemplateId);
    let interpDisplay = '';
    
    if (this.selectedInterpretationCode) {
      const map: Record<string, string> = {
        'N': 'Normal (N)',
        'H': 'High (H)',
        'L': 'Low (L)',
        'A': 'Abnormal (A)',
        'HH': 'Critical High (HH)',
        'LL': 'Critical Low (LL)',
        'AA': 'Critical Abnormal (AA)',
        'POS': 'Positive (POS)',
        'NEG': 'Negative (NEG)',
        'IND': 'Indeterminate (IND)',
        'RR': 'Reactive (RR)',
        'NR': 'Non-reactive (NR)',
        'S': 'Susceptible (S)',
        'R': 'Resistant (R)',
        'I': 'Intermediate (I)'
      };
      interpDisplay = map[this.selectedInterpretationCode] || '';
    }

    this.dialogRef.close({
      selectedTemplate: template,
      interpretationCode: this.selectedInterpretationCode,
      interpretationDisplay: interpDisplay
    });
  }
}
